import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  HistoryPageNavbarProps,
  HistoryPageNavbarRequestInfo,
  HistoryPageNavbarScoreItem,
} from "../types";

import { toggleSharableRequest } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { usePromptRegistry, useRequestGroups, useToggleStarredRequest } from "@/queries";
import { formatTimestampUTC } from "@/utils/timestamp";

const DEFAULT_SHARE_ORIGIN = "https://dashboard.promptlayer.com";

const hasDefaultScore = (scoreItem: HistoryPageNavbarScoreItem) =>
  Object.prototype.hasOwnProperty.call(scoreItem, "default");

const getRequestScore = (requestInfo: HistoryPageNavbarRequestInfo | null | undefined) => {
  const defaultScore = requestInfo?.scores?.find(hasDefaultScore)?.default;
  return typeof defaultScore === "number" ? defaultScore : null;
};

const getOtherScores = (requestInfo: HistoryPageNavbarRequestInfo | null | undefined) =>
  (requestInfo?.scores ?? []).filter(scoreItem => !hasDefaultScore(scoreItem));

export const useHistoryPageNavbarState = ({ requestId, requestInfo }: HistoryPageNavbarProps) => {
  const authContext = useAuth();
  const navigate = useNavigate();
  const userContext = useUser();
  const userToken = authContext?.userToken ?? "";
  const isLoggedIn = Boolean(userToken && userContext?.user);

  const requestGroupsQuery = useRequestGroups(requestId, userToken);
  const promptRegistry = usePromptRegistry(userToken, requestInfo?.prompt_id ?? undefined, false);
  const { mutate: toggleStarredRequest, isLoading: isLoadingToggleStarred } =
    useToggleStarredRequest();

  const requestScore = getRequestScore(requestInfo);
  const otherScoresList = getOtherScores(requestInfo);
  const loggedTimeElement = requestInfo?.request_start_time
    ? formatTimestampUTC(requestInfo.request_start_time, {
        showRelativeWithinHours: Number.POSITIVE_INFINITY,
      })
    : "";
  const loggedTimeTitle = requestInfo?.request_start_time
    ? formatTimestampUTC(requestInfo.request_start_time, {
        includeSeconds: true,
        showRelativeWithinHours: 0,
      })
    : "";

  const [loading, setLoading] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [resetScore, setResetScore] = useState(requestScore === null);
  const [score, setScore] = useState<number[]>(requestScore !== null ? [requestScore] : []);
  const [sharingEnabled, setSharingEnabledState] = useState(Boolean(requestInfo?.is_sharable));

  useEffect(() => {
    setScore(requestScore !== null ? [requestScore] : []);
    setResetScore(requestScore === null);
  }, [requestId, requestScore]);

  useEffect(() => {
    setSharingEnabledState(Boolean(requestInfo?.is_sharable));
  }, [requestId, requestInfo?.is_sharable]);

  const shareOrigin = typeof window !== "undefined" ? window.location.origin : DEFAULT_SHARE_ORIGIN;
  const sharingUrl = useMemo(
    () => `${shareOrigin}/share/${requestInfo?.share_hash ?? ""}`,
    [requestInfo?.share_hash, shareOrigin],
  );
  const sharingUrlDisplay = useMemo(() => sharingUrl.replace(/^https?:\/\//, ""), [sharingUrl]);

  const handleToggleSharableRequest = useCallback(
    async (newVal: boolean) => {
      if (!authContext) {
        return;
      }

      setLoading(true);

      try {
        const response = await toggleSharableRequest(authContext, Number(requestId), newVal);

        if (response.status === 200) {
          const data = (await response.json()) as { success?: boolean };
          if (data.success) {
            setSharingEnabledState(newVal);
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [authContext, requestId],
  );

  const handleSetSharingEnabled = useCallback(
    (newVal: boolean) => {
      void handleToggleSharableRequest(newVal);
    },
    [handleToggleSharableRequest],
  );

  const handleToggleStar = useCallback(
    (newVal: boolean) => {
      if (!authContext?.userToken) {
        return;
      }

      toggleStarredRequest({
        id: requestId,
        newVal,
        userToken: authContext.userToken,
      });
    },
    [authContext?.userToken, requestId, toggleStarredRequest],
  );

  const handleCopyRequestId = useCallback(() => {
    if (typeof navigator === "undefined" || requestInfo?.id === undefined) {
      return;
    }

    void navigator.clipboard.writeText(String(requestInfo.id));
  }, [requestInfo?.id]);

  const templateFromRegistryInfo = promptRegistry.data
    ? {
        id: promptRegistry.data.id,
        prompt_name: promptRegistry.data.prompt_name,
      }
    : null;

  return {
    activeWorkspaceId: userContext?.activeWorkspaceId,
    isLoadingToggleStarred,
    isLoggedIn,
    isShareDialogOpen,
    loading,
    loggedTimeElement,
    loggedTimeTitle,
    onCopyRequestId: handleCopyRequestId,
    onNavigateHome: () => navigate("/"),
    onOpenShareDialog: () => setIsShareDialogOpen(true),
    onSetSharingEnabled: handleSetSharingEnabled,
    onToggleStar: handleToggleStar,
    otherScoresList,
    promptRegistry: {
      isLoading: promptRegistry.isLoading,
      template: templateFromRegistryInfo,
    },
    requestGroups: requestGroupsQuery.data?.groups ?? [],
    requestScore,
    resetScore,
    score,
    setIsShareDialogOpen,
    setResetScore,
    setScore,
    sharingEnabled,
    sharingUrl,
    sharingUrlDisplay,
  };
};
